/**
 * GridClash Beta — Global Leaderboard Client
 *
 * Calls /api/leaderboard (Netlify serverless function) instead of Supabase
 * directly. The function acts as a secure proxy — Supabase credentials
 * never leave the server, and there are no CORS/allowlist issues.
 */

const API_BASE = "/api/leaderboard";
const REQUEST_TIMEOUT_MS = 5000;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GlobalScore {
  id: string;
  name: string;
  score: number;
  tier: string;
  mode: "random" | "deterministic";
  time_secs: number;
  created_at: string;
  /** Equipped player title at submit time; absent on pre-5D rows. */
  title?: string | null;
}

export type TimeRange = "daily" | "weekly" | "alltime";

export interface SubmitResult {
  success: boolean;
  rank: number | null;
  error?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Always enabled — the proxy function is always deployed with the game. */
export function isGlobalLeaderboardEnabled(): boolean {
  return true;
}

export async function fetchTopScores(
  tier: string,
  range: TimeRange,
  limit = 10
): Promise<GlobalScore[]> {
  try {
    const url = `${API_BASE}?tier=${encodeURIComponent(tier)}&range=${range}&limit=${limit}`;
    const res = await fetchWithTimeout(url, { method: "GET" });
    if (!res.ok) return [];
    const data = await res.json() as unknown;
    if (!Array.isArray(data)) return [];
    return data.filter((e): e is GlobalScore =>
      typeof e === "object" && e !== null &&
      typeof (e as GlobalScore).name === "string" &&
      typeof (e as GlobalScore).score === "number"
    );
  } catch {
    return [];
  }
}

export async function submitScore(params: {
  name: string;
  score: number;
  tier: string;
  mode: "random" | "deterministic";
  timeSecs: number;
  /** Equipped player title — validated server-side against the allowlist. */
  title?: string;
}): Promise<SubmitResult> {
  const sanitized = params.name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  if (sanitized.length === 0) {
    return { success: false, rank: null, error: "Name must contain at least one letter or number" };
  }

  try {
    const res = await fetchWithTimeout(API_BASE, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name:      sanitized,
        score:     params.score,
        tier:      params.tier,
        mode:      params.mode,
        time_secs: params.timeSecs,
        title:     params.title || null,
      }),
    });

    const data = await res.json() as Record<string, unknown>;

    if (!res.ok) {
      return { success: false, rank: null, error: (data["error"] as string) ?? "Could not save score" };
    }

    return {
      success: true,
      rank: typeof data["rank"] === "number" ? data["rank"] : null,
    };
  } catch {
    return { success: false, rank: null, error: "Network error — score saved locally only" };
  }
}

const LAST_NAME_KEY = "gridcrash.global.lastName";

export function getLastUsedName(): string {
  try { return localStorage.getItem(LAST_NAME_KEY) ?? ""; } catch { return ""; }
}

export function setLastUsedName(name: string): void {
  try { localStorage.setItem(LAST_NAME_KEY, name.toUpperCase().slice(0, 8)); } catch { /* ignore */ }
}

export function validateName(input: string): { valid: boolean; cleaned: string; error?: string } {
  const cleaned = input.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  if (cleaned.length === 0) {
    return { valid: false, cleaned, error: "Name must have at least one letter or number (max 8 chars)" };
  }
  return { valid: true, cleaned };
}
